import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import apiService from '../services/api';
import { getReaderReadingHistory } from '../services/readerService';
import { getUniqueChapterNumber } from '../services/bibleService';
import { getFromStorage, STORAGE_KEYS } from '../utils/storageUtils';

function Bible() {
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeChapter, setActiveChapter] = useState(null);
  const [selectedReader, setSelectedReader] = useState('');
  const [readChapters, setReadChapters] = useState([]);
  const [loadingReadHistory, setLoadingReadHistory] = useState(false);
  const [showLegend, setShowLegend] = useState(false);
  const navigate = useNavigate();
  const { book, chapter } = useParams();
  const legendRef = useRef(null);
  const legendButtonRef = useRef(null);

  // Fetch all Bible books
  useEffect(() => {
    const fetchBooks = async () => {
      try {
        setLoading(true);
        setError(null);
        const response = await apiService.get('/bible/books');
        setBooks(response.books || []);
        
        // If a book and chapter are specified in the URL, set active chapter
        if (book && chapter) {
          setActiveChapter({ book, chapter });
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Failed to fetch Bible books:', err);
        setError('Failed to load Bible books. Please try again later.');
        setLoading(false);
      }
    };
    
    fetchBooks();
  }, [book, chapter]);

  // Initialize selected reader from localStorage
  useEffect(() => {
    const savedReader = getFromStorage(STORAGE_KEYS.SELECTED_READER);
    if (savedReader) {
      setSelectedReader(savedReader);
    }
  }, []);

  // Listen for reader changes from the ReaderSelector component
  useEffect(() => {
    const handleReaderChange = (event) => {
      const { readerId } = event.detail;
      console.log('Bible - Reader changed event received:', readerId);
      setSelectedReader(readerId);
    };
    
    window.addEventListener('readerChanged', handleReaderChange);
    
    return () => {
      window.removeEventListener('readerChanged', handleReaderChange);
    };
  }, []);

  // Fetch reading history when a reader is selected
  useEffect(() => {
    const fetchReadingHistory = async () => {
      if (!selectedReader) return;
      
      try {
        setLoadingReadHistory(true);
        const response = await getReaderReadingHistory(selectedReader);
        
        // Extract the chapter IDs from the reading history
        const chaptersRead = response.chaptersRead || [];
        setReadChapters(chaptersRead.map(entry => ({
          book: entry.book,
          chapter: entry.chapter,
          chapterId: entry.chapter_id,
          readDate: new Date(entry.read_date || entry.readDate || Date.now()),
          hasNotes: entry.notes && entry.notes.trim().length > 0
        })));
        
        setLoadingReadHistory(false);
      } catch (err) {
        console.error('Failed to fetch reading history:', err);
        setLoadingReadHistory(false);
      }
    };
    
    fetchReadingHistory();
  }, [selectedReader]);

  // Close legend when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showLegend && 
          legendRef.current && 
          !legendRef.current.contains(event.target) &&
          legendButtonRef.current && 
          !legendButtonRef.current.contains(event.target)) {
        setShowLegend(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLegend]);

  // Handle chapter selection
  const handleChapterSelect = (bookName, chapterNum) => {
    console.log(`Selecting chapter: ${bookName} ${chapterNum}`);
    setActiveChapter({ book: bookName, chapter: chapterNum });
    const targetUrl = `/bible/${bookName}/${chapterNum}`;
    console.log(`Navigating to: ${targetUrl}`);
    navigate(targetUrl);
  };

  // Calculate the age of a read entry in days
  const calculateReadAge = (readDate) => {
    const now = new Date();
    const diffTime = Math.abs(now - readDate);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Calculate color based on read date (solid green for 30 days, then fading to white over remaining 335 days)
  const calculateReadColor = (readDate) => {
    const age = calculateReadAge(readDate);
    const solidGreenDays = 30; // Stay solid green for 30 days
    const maxAge = 365; // 365 days = 1 year
    
    // If less than 30 days old, use solid green
    if (age <= solidGreenDays) {
      return '#4caf50'; // Solid green
    }
    
    // Calculate how "fresh" the reading is after the solid period
    // 1.0 = just past solid period, 0.0 = read a year ago or more
    const adjustedAge = age - solidGreenDays;
    const remainingFadePeriod = maxAge - solidGreenDays;
    const freshness = Math.max(0, 1 - (adjustedAge / remainingFadePeriod));
    
    // Calculate RGB values for the gradient from green (#4caf50) to white (#ffffff)
    const r = Math.round(76 + (255 - 76) * (1 - freshness));
    const g = Math.round(175 + (255 - 175) * (1 - freshness));
    const b = Math.round(80 + (255 - 80) * (1 - freshness));
    
    return `rgb(${r}, ${g}, ${b})`;
  };

  // Determine if text should be dark or light based on background color
  const getTextColorForBackground = (backgroundColor) => {
    // For solid green or known colors
    if (backgroundColor === '#4caf50') {
      return 'white';
    }
    
    // For RGB values, extract components
    const rgbMatch = backgroundColor.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
    if (rgbMatch) {
      const r = parseInt(rgbMatch[1]);
      const g = parseInt(rgbMatch[2]);
      const b = parseInt(rgbMatch[3]);
      
      // Calculate relative luminance (perceived brightness)
      // Formula: 0.299*R + 0.587*G + 0.114*B
      const brightness = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
      
      // Use dark text for bright backgrounds, light text for dark backgrounds
      // Lowering the threshold to ensure better contrast
      return brightness > 0.5 ? '#000000' : '#ffffff';
    }
    
    // Default to black text
    return '#000000';
  };

  // Check if a chapter has been read and get its read date
  const getChapterReadInfo = (bookName, chapterNum) => {
    const readChapter = readChapters.find(
      chapter => chapter.book === bookName && parseInt(chapter.chapter) === chapterNum
    );
    
    if (readChapter) {
      const backgroundColor = calculateReadColor(readChapter.readDate);
      return {
        isRead: true,
        readDate: readChapter.readDate,
        color: backgroundColor,
        textColor: getTextColorForBackground(backgroundColor),
        hasNotes: readChapter.hasNotes
      };
    }
    
    return { isRead: false };
  };

  // Generate chapter grid for a book
  const renderChapterGrid = (book) => {
    const chapters = Array.from({ length: book.chapters }, (_, i) => i + 1);
    
    return (
      <div className="chapter-grid">
        {chapters.map(chapterNum => {
          const readInfo = getChapterReadInfo(book.name, chapterNum);
          const isActive = activeChapter && 
                          activeChapter.book === book.name && 
                          parseInt(activeChapter.chapter) === chapterNum;
          
          return (
            <button
              key={chapterNum}
              className={`chapter-button ${isActive ? 'active' : ''} ${readInfo.isRead ? 'read' : ''} ${readInfo.hasNotes ? 'has-notes' : ''}`}
              onClick={() => handleChapterSelect(book.name, chapterNum)}
              style={readInfo.isRead && !isActive ? { 
                backgroundColor: readInfo.color,
                color: readInfo.textColor,
                fontWeight: 'bold'
              } : {}}
              title={readInfo.hasNotes ? `${book.name} ${chapterNum} - Has notes` : `${book.name} ${chapterNum}`}
            >
              {chapterNum}
              {readInfo.hasNotes && !isActive && (
                <span className="notes-indicator"></span>
              )}
            </button>
          );
        })}
      </div>
    );
  };

  // Group books by testament
  const groupedBooks = books.reduce((acc, book) => {
    if (!acc[book.testament]) {
      acc[book.testament] = [];
    }
    acc[book.testament].push(book);
    return acc;
  }, {});

  // Generate a color gradient legend
  const renderColorLegend = () => {
    const legendItems = [
      { days: 0, label: 'Today to 30 days' },
      { days: 60, label: '2 months ago' },
      { days: 120, label: '4 months ago' },
      { days: 240, label: '8 months ago' },
      { days: 365, label: '1 year ago' }
    ];
    
    return (
      <div className="color-legend-popover" ref={legendRef}>
        <div className="color-legend-content">
          <div className="color-legend-header">
            <h5>Reading History Color Guide</h5>
            <button 
              type="button" 
              className="btn-close" 
              onClick={() => setShowLegend(false)} 
              aria-label="Close"
            ></button>
          </div>
          <div className="color-legend-body">
            <p className="mb-2">Colors indicate when chapters were read:</p>
            <div className="d-flex flex-wrap">
              {legendItems.map((item, index) => {
                const date = new Date();
                date.setDate(date.getDate() - item.days);
                const color = calculateReadColor(date);
                const textColor = getTextColorForBackground(color);
                
                return (
                  <div key={index} className="legend-item d-flex flex-column align-items-center me-3 mb-2">
                    <div 
                      className="legend-color" 
                      style={{ 
                        backgroundColor: color,
                        color: textColor,
                        width: '20px',
                        height: '20px',
                        borderRadius: '4px',
                        border: '1px solid #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontSize: '10px'
                      }}
                    >
                      {index === 0 ? '✓' : ''}
                    </div>
                    <small className="legend-text">{item.label}</small>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-3">Loading Bible data...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div className="bible-container">
      <h1 className="bible-title">Bible Explorer</h1>
      
      {/* Reading Progress Info */}
      {loadingReadHistory && (
        <div className="text-center mt-2 mb-4">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading reading history...</span>
          </div>
          <span className="ms-2">Loading reading history...</span>
        </div>
      )}
      
      {selectedReader && readChapters.length > 0 && (
        <div className="reading-progress mb-4">
          <div className="d-flex align-items-center">
            <div className="chapter-button read me-2" style={{ width: '20px', height: '20px' }}></div>
            <span>= Read chapters ({readChapters.length} total)</span>
            <button 
              className="btn btn-link p-0 ms-2" 
              onClick={() => setShowLegend(!showLegend)}
              aria-label="Show color legend"
              title="What do the colors mean?"
              ref={legendButtonRef}
            >
              <i className="bi bi-question-circle"></i>
            </button>
            
            {/* Color Legend Popover */}
            {showLegend && renderColorLegend()}
          </div>
        </div>
      )}
      
      {/* Reading Reports Link */}
      {selectedReader && (
        <div className="reading-reports-link">
          <Link to={`/reading-reports/${selectedReader}`} className="btn btn-primary">
            View Full Reading Reports
          </Link>
        </div>
      )}
      
      <div className="bible-content">
        <div className="testament-section">
          <h2>Old Testament</h2>
          <div className="books-list">
            {groupedBooks['Old']?.map(book => (
              <div key={book.name} className="book-item">
                <h3 className="book-title">{book.name}</h3>
                {renderChapterGrid(book)}
              </div>
            ))}
          </div>
        </div>
        
        <div className="testament-section">
          <h2>New Testament</h2>
          <div className="books-list">
            {groupedBooks['New']?.map(book => (
              <div key={book.name} className="book-item">
                <h3 className="book-title">{book.name}</h3>
                {renderChapterGrid(book)}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Bible; 